/* React */
import React, { useEffect, useState } from "react";

/* React Redux */
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/redux/store";
import { toggleInsightsMenu } from "src/redux/features/menu/secondary-menu/secondaryMenuSlice";
import { setPageTitle } from "src/redux/features/page-title/pageTitleSlice";

/* React Router */
import { useLocation } from "react-router";

/* Ionic */
import {
  IonMenuButton,
  IonIcon,
  IonButton,
} from "@ionic/react";
import {
  close,
  settings,
  sparkles,
} from "ionicons/icons";

/* Utils */
import { pathToTitleMapper } from "src/utils/helper/common/pathToTitleMapper";
import { useToggleSecondaryMenu } from "src/utils/hooks/menu/useToggleSecondaryMenu";
import { createScssProps } from "src/components/app-toolbar/createScssProps";

/* Components */
import StatusContainer from "src/components/admin-dashboard/status-container/StatusContainer";
import UserMenu from "src/components/menus/user-menu/UserMenu";
import AssistantPanel from "src/components/ai-assistant/assistant-panel/AssistantPanel";

/* Constants */
import { Paths } from "src/shared/constants/common/Paths";
import { UserRoles } from "src/shared/constants/auth/User";

/* Stylesheet */
import styles from "./AppToolbar.module.scss";
import { Status } from "src/shared/constants/admin-dashboard/Settings";

const AppToolbar: React.FC = () => {
  const { pathname } = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const scrollState = useSelector(
    (state: RootState) => state.scroll
  );

  const { scrollPosition } = scrollState;

  useEffect(() => {
    /**
     * Set the page title based on the current path.
     * The `pathToTitleMapper` maps paths to titles.
     */
    const title = pathToTitleMapper[pathname];
    dispatch(setPageTitle(title));
  }, [dispatch, pathname]);

  /**
   * Custom hook to check if the insights menu can be toggled.
   * The insights menu is disabled when the user is on a non-insights page.
   */
  useToggleSecondaryMenu();

  const secondaryMenuState = useSelector(
    (state: RootState) => state.secondaryMenu
  );

  const { menuType, isOpen, isDisabled } =
    secondaryMenuState;

  const pageTitleState = useSelector(
    (state: RootState) => state.pageTitle
  );

  const scssProps = createScssProps(
    pathname,
    scrollPosition
  );

  const settingsState = useSelector(
    (state: RootState) => state.settings
  );

  const { devOptions } = settingsState;

  const userRoles = useSelector(
    (state: RootState) => state.auth.roles
  );

  const [isAssistantOpen, setIsAssistantOpen] =
    useState<boolean>(false);

  /**
   * The assistant configures the insights controls, so it is only
   * offered on the insights pages — and only to users holding the
   * agent role.
   *
   * This is the one way in: the standalone AI Assistant page and its
   * menu entry are gone while the agent's /chat endpoint is a stub, so
   * this check is no longer one of two doing the same job. The agent
   * requires the same role on /spec regardless, so a user without it
   * gets a 403 rather than a configuration.
   */
  const canUseAssistant =
    pathname.startsWith(Paths.INSIGHTS) &&
    userRoles.includes(UserRoles.INSIGHTS_AGENT);

  /**
   * The toolbar outlives route changes, so a panel left open would
   * reappear by itself the next time the user happens onto an insights
   * page. Closing it when the assistant becomes unavailable keeps the
   * panel something the user opens deliberately.
   */
  useEffect(() => {
    if (!canUseAssistant) {
      setIsAssistantOpen(false);
    }
  }, [canUseAssistant]);

  return (
    <div
      style={{
        position: scssProps.position,
        top: scssProps.top,
        left: scssProps.left,
        width: scssProps.width,
        background: scssProps.backgroundColor,
        color: scssProps.color,
        zIndex: scssProps.zIndex,
      }}
      className={`${styles.app_toolbar} ${
        scrollPosition > 200 ? styles.scrolled : ""
      }`}
    >
      <div className={styles.menu_button_container}>
        <IonMenuButton
          style={{ "--color": scssProps.color }}
          data-testid="main-menu-button"
          className={styles.menu_button}
          menu="main-menu"
        />
        {pageTitleState.title ? (
          <div className={styles.page_title}>
            {pageTitleState.title}
          </div>
        ) : undefined}
      </div>
      <div className={styles.options_container}>
        <StatusContainer
          status={
            devOptions.isDevMode
              ? Status.DEVELOPMENT
              : Status.DEFAULT
          }
        />
        {canUseAssistant ? (
          <IonButton
            fill="clear"
            className={styles.assistant_button}
            data-testid="assistant-button"
            aria-label="Open the AI Assistant"
            onClick={() =>
              setIsAssistantOpen((isOpen) => !isOpen)
            }
          >
            <IonIcon
              className={styles.icon}
              icon={isAssistantOpen ? close : sparkles}
            />
          </IonButton>
        ) : undefined}
        {!isDisabled ? (
          <IonMenuButton
            className={`${styles.filter_menu_button} ${
              isOpen ? styles.open : ""
            }`}
            menu={menuType}
            onClick={() =>
              dispatch(toggleInsightsMenu(menuType))
            }
          >
            <IonIcon
              className={styles.icon}
              icon={isOpen ? close : settings}
            />
          </IonMenuButton>
        ) : undefined}
        <UserMenu />
      </div>
      {canUseAssistant && isAssistantOpen ? (
        <AssistantPanel
          onClose={() => setIsAssistantOpen(false)}
        />
      ) : undefined}
    </div>
  );
};

export default AppToolbar;
